import React, { useState, useEffect, useCallback } from "react";
import "./../elementPages.less";
import materialsDB from '@/constants/materialsDB';
import { rebuildFormula } from '@/config/func';
import dayjs from "dayjs";
import { Button, Dropdown, Checkbox, Tooltip, Select } from "antd";
import { replaceFormulaStr } from "@/config/func";
import { SettingOutlined } from "@ant-design/icons";
import { useKeyComponents } from "../../ResultPageFrame";
import { formatBySig, resolveSig, formatErrorPercent, isNumValue } from "@/utils/numberFormat";



// 定义类型接口
interface StreamProperty {
  name: string;
  unit?: string;
  [key: string]: any;
}

interface SingleStreamData {
  streamName: string;
  originStreamName: string; // 新增：原始流股名称，用于匹配对比项
  phase: string;
  molarFlow: any;
  massFlow: number;
  temperature: number;
  pressure: number;
  vaporFrac: number;
  enthalpy: number;
  volFlowNm3h: any;
  volFlowM3h: any;
  volFlowLh: any;
  // 四种类型的组分数据
  molarFrac: { [materialName: string]: number };
  massFrac: { [materialName: string]: number };
  molarFlowComp: { [materialName: string]: number };
  massFlowComp: { [materialName: string]: number };
  massConc: { [materialName: string]: number };
  // 对比项数据
  compareData: {
    [compareId: string]: {
      temperature: number;
      pressure: number;
      molarFlow: any;
      massFlow: number;
      vaporFrac: any;
      enthalpy: string;
      volFlowNm3h: any;
      volFlowM3h: any;
      volFlowLh: any;
      molarFrac: { [materialName: string]: number };
      massFrac: { [materialName: string]: number };
      // 对比项可能缺少总摩尔流量（运行数据），此时组分摩尔流量视为缺失（null → 显示 "-"）
      molarFlowComp: { [materialName: string]: number | null };
      massFlowComp: { [materialName: string]: number };
      massConc: { [materialName: string]: number };
    };
  };
}

interface DataType {
  温度: {
    T_K: StreamProperty[];
    T_C: StreamProperty[];
  };
  压力: {
    P_Pa: StreamProperty[];
    P_bar: StreamProperty[];
    P_KPa: StreamProperty[];
    P_MPa: StreamProperty[];
    P_atm: StreamProperty[];
  };
  inputStreams: SingleStreamData[];
  outputStreams: SingleStreamData[];
  // 材料名称列表，用于渲染表头
  materialNames: string[];
}

interface CompareItem {
  id: number;
  type: "design" | "actual" | "task" | "otherCalc" | "timeRange";
  name?: string; // 与下拉列表一致的名称（实际值存储项=工况名）
  values: {
    mix_input: {
      streams: any[];
    };
    name: string;
    streams?: any[]; // 兼容第二份代码的结构
    fullOutput?: { streams: any[]; elements?: any[] }; // 各页面统一上报的输出换算数据
  };
  created_at?: string; // otherCalc 类型需要的时间字段
  startTime?: string; // timeRange 类型需要的时间范围
  endTime?: string; // timeRange 类型需要的时间范围
  date?: string; // 实际运行数据日期（未选时间段时表头只显示日期）
  fullDay?: boolean; // 实际运行数据是否为全天（未选时间段）
}

interface CalculateTask3DYCZTableProps {
  inputData: any;
  resultsData: any;
  streamsData: any;
  compareItems?: CompareItem[];
  streamDescMap?: Record<string, string>;
  streamNodeMap?: Record<string, { fromName: string; toName: string }>;
  elementDescMap?: Record<string, string>;
  tempUnit?: string;
  pressUnit?: string;
}

// 温度/压力单位配置
const T_ITEMS = [
  { label: "K", value: "T_K" },
  { label: "℃", value: "T_C" },
];

const P_ITEMS = [
  { label: "Pa", value: "P_Pa" },
  { label: "bar", value: "P_bar" },
  { label: "KPa", value: "P_KPa" },
  { label: "MPa", value: "P_MPa" },
  { label: "atm", value: "P_atm" },
];

// 数据类型配置（用于切换）
const DATA_TYPE_ITEMS = [
  { label: "摩尔分率", value: "摩尔分率", unit: "" },
  { label: "质量分率", value: "质量分率", unit: "" },
  { label: "质量流量", value: "质量流量", unit: "kg/h" },
  { label: "摩尔流量", value: "摩尔流量", unit: "kmol/h" },
  { label: "质量浓度", value: "质量浓度", unit: "mg/Nm³" },
];

// 安全的 toFixed 方法
const safeToFixed = (value: any, digits: number = 2): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return "-";
  }
  return Number(value).toFixed(digits);
};

// 计算误差百分比
const calculateErrorPercent = (compareValue: number, calcValue: number): string => {
  // 判断设计值/实际值/任务值和计算值是否都为 0
  if (compareValue === 0 && calcValue === 0) {
    return "0%";
  }
  // 如果计算值不为 0 但设计值/实际值/任务值为 0
  if (compareValue === 0 && calcValue !== 0) {
    return "100%";
  }
  // 正常计算误差百分比：(计算值 - 设计值/实际值/任务值) ÷ 设计值/实际值/任务值
  const errorPercent = ((calcValue - compareValue) / compareValue) * 100;
  //非 0 但极小的误差显示 <0.01%，避免出现"0.00% 却被标红"的矛盾
  return formatErrorPercent(errorPercent);
};

// 判断当前值与对比值是否有差异（用于整行标红，与输入参数对比高亮方式一致）
const valuesDiffer = (current: any, compare: any): boolean => {
  // 缺失值：- / null / undefined
  const curMissing = current === "-" || current === null || current === undefined;
  const cmpMissing = compare === "-" || compare === null || compare === undefined;
  // 任一侧缺失（页面显示为 "-"）都不算差异，不标红
  if (curMissing || cmpMissing) return false;
  const curStr = String(current);
  const cmpStr = String(compare);
  const curNum = parseFloat(curStr);
  const cmpNum = parseFloat(cmpStr);
  const curIsNum = !isNaN(curNum);
  const cmpIsNum = !isNaN(cmpNum);
  if (curIsNum && cmpIsNum) {
    // 数值比较：相对容差比较，忽略浮点噪声（相对误差 < 0.0000001% 视为相等，与显示一致）
    if (curNum === cmpNum) return false;
    const tolerance = Math.max(Math.abs(curNum), Math.abs(cmpNum)) * 1e-9;
    return Math.abs(curNum - cmpNum) > tolerance;
  }
  // 非数值：字符串直接比较
  return curStr !== cmpStr;
};

export default function CalculateTask3DYCZTable({
  inputData,
  resultsData,
  streamsData,
  compareItems = [],
  streamDescMap = {},
  streamNodeMap = {},
  elementDescMap = {},
  tempUnit,
  pressUnit,
}: CalculateTask3DYCZTableProps) {

  const [selectT, setSelectT] = useState("T_C");
  const [selectP, setSelectP] = useState("P_MPa");
  // 液相体积流量单位切换（m³/h / L/h）
  const [selectVolFlow, setSelectVolFlow] = useState("volFlowM3h");
  // 使用外部传入的温压单位，若无则使用内部状态
  const effectiveSelectT = tempUnit ? (tempUnit === "K" ? "T_K" : "T_C") : selectT;
  const effectiveSelectP = pressUnit ? `P_${pressUnit}` : selectP;
  const [selectButton, setSelectButton] = useState("摩尔分率");
  const [data, setData] = useState<DataType | null>(null);
  const [numDigit, setNumDigit] = useState(4);
  const [showAllData, setShowAllData] = useState(true);
  const { keyComponents, setKeyComponents, keyComponentsEverSet } = useKeyComponents();

  // 查找材料名称
  const findMaterial = (item: any) => {
    const ma = materialsDB.find((m: any) => m.cas === item.cas);
    if (ma) {
      return rebuildFormula(ma.Molecular_formula);
    } else {
      return rebuildFormula(item.name);
    }
  };

  // 解析对比项流股数据
  const parseCompareStreamData = useCallback((
    stream: any,
    streamsData: any,
    compareItem: CompareItem
  ) => {
    const molarFrac: { [key: string]: number } = {};
    const massFrac: { [key: string]: number } = {};
    const molarFlowComp: { [key: string]: number | null } = {};
    const massFlowComp: { [key: string]: number } = {};
    const massConc: { [key: string]: number } = {};

    // 质量流量：缺失（null）时保留 null，表格中显示为 "-"，不用 0 替换
    const compareMassFlow = stream.params?.f ?? null;
    // 运行数据（实际值）不提供总摩尔流量 f_mol_per_h，缺失时该对比项的组分摩尔流量整体视为缺失（显示 "-"），
    // 不能按 0 计算：否则单元格会显示 0，并被误判为与计算值有差异而标红
    const rawMolPerH = stream.f_mol_per_h;
    const compareMolPerH: number | null =
      rawMolPerH === null || rawMolPerH === undefined || rawMolPerH === "" || isNaN(Number(rawMolPerH))
        ? null
        : Number(rawMolPerH);
    const compareNm3PerH = stream.f_nm3_per_h || stream.f_m3_per_h || 1;
    // 设计值可能没有 mass_frac 数据
    const hasMassFrac = stream.mass_frac != null && stream.mass_frac.length > 0;

    streamsData.materials.forEach((mat: any, idx: number) => {
      const matName = findMaterial(mat);
      const compareMat = stream.materials[idx];
      // 摩尔分率：保留 null（缺失组分），显示为 "-"
      const mf = compareMat?.molar_frac ?? null;
      if (idx < 3) console.log(`  idx=${idx} mat=${matName} compareMat=`, compareMat, `mf=${mf}`);
      molarFrac[matName] = mf;
      // 质量分率 - 使用实际值（设计值可能没有 mass_frac，缺失时不填充，对比时显示 "-"）
      const mFrac = stream.mass_frac?.[idx];
      if (mFrac !== undefined) {
        massFrac[matName] = mFrac;
      }
      // 摩尔流量（kmol/h）：总摩尔流量或组分摩尔分率缺失时，该组分摩尔流量也视为缺失（显示 "-"，不参与差异判定）
      molarFlowComp[matName] = compareMolPerH === null || mf === null ? null : (mf * compareMolPerH) / 1000;
      // 质量流量（kg/h）/ 质量浓度 — 设计值无 mass_frac 时不计算
      if (hasMassFrac && mFrac !== undefined) {
        massFlowComp[matName] = mFrac * compareMassFlow;
        massConc[matName] = (massFlowComp[matName] * 1e6) / compareNm3PerH;
      }
    });

    return {
      temperature: stream.params.t || "-",
      pressure: (() => {
        const p = stream.params.p || "-";
        const unit = stream.params.p_unit || "MPa";
        switch (unit) {
          case "Pa": return p;
          case "bar": return p * 100000;
          case "KPa": return p * 1000;
          case "MPa": return p * 1000000;
          case "atm": return p * 101325;
          default: return p;
        }
      })(),
      // 单位换算：从 mol/h 转换为 kmol/h（除以 1000）；缺失总摩尔流量时显示 "-"
      molarFlow: compareMolPerH !== null && compareMolPerH > 0 ? compareMolPerH / 1000 : "-",
      massFlow: compareMassFlow,
      vaporFrac: stream.vf ?? "-",
  // vaporFrac: stream.params.phase === "V" ? 1 : 0,
      enthalpy: stream.H ?? "-",
      volFlowNm3h: stream.f_nm3_per_h ?? "-",
      volFlowM3h: stream.f_m3_per_h ?? "-",
      volFlowLh: stream.f_l_per_h ?? "-",
      molarFrac,
      massFrac,
      molarFlowComp,
      massFlowComp,
      massConc,
    };
  }, []);

  // 解析主数据的流股数据，并关联对比项数据
  const parseStreamData = useCallback((
    streamList: any[],
    streamsData: any,
    isOutput: boolean = false,
    streamRules?: { [key: string]: string } // 流股名称到 rule 的映射
  ): SingleStreamData[] => {
    return streamList.map((stream: any) => {
      const molarFrac: { [key: string]: number } = {};
      const massFrac: { [key: string]: number } = {};
      const molarFlowComp: { [key: string]: number } = {};
      const massFlowComp: { [key: string]: number } = {};
      const massConc: { [key: string]: number } = {};
      const compareData: SingleStreamData["compareData"] = {};

      streamsData.materials.forEach((mat: any, idx: number) => {
        const matName = findMaterial(mat);
        // 主数据
        const mf = stream["molar_frac"]?.[idx] ?? 0;
        molarFrac[matName] = mf;
        const mFrac = stream["mass_frac"]?.[idx] ?? 0;
        massFrac[matName] = mFrac;
        molarFlowComp[matName] = mf * (stream["f_mol_per_h"] ?? 0)/1000;
        massFlowComp[matName] = mFrac * (stream["f_kg_per_h"] ?? 0);
        const volFlow = stream["f_nm3_per_h"] || stream["f_m3_per_h"] || 1;
        massConc[matName] = (massFlowComp[matName] * 1e6) / volFlow;

        // 对比项数据
        compareItems.forEach((item) => {
          // 兼容两种对比项结构；优先用各页面统一上报的输出换算数据（fullOutput）
        const compareStream = item.values?.fullOutput?.streams?.find((s: any) => s.name === stream.name) 
          || item.values?.mix_input?.streams?.find((s: any) => s.name === stream.name) 
          || item.values?.streams?.find((s: any) => s.name === stream.name);
          if (compareStream) {
            if (!compareData[item.id]) {
              compareData[item.id] = parseCompareStreamData(compareStream, streamsData, item);
            }
          }
        });
      });
      // 获取流股的 rule（塔顶/塔底标识）
      const rule = streamRules?.[stream.name];
      
      return {
        streamName: `${stream.name}${isOutput ? (rule === "D" ? "（出料塔顶）" : "（出料塔底）") : "（进料）"}`,
        originStreamName: stream.name, // 新增原始流股名称
        phase: stream["phase"] === "V" ? "气相" : "液相",
        // 单位换算：从 mol/h 转换为 kmol/h（除以 1000）
        molarFlow: (stream["f_mol_per_h"] / 1000) || "-",
        massFlow: stream["f_kg_per_h"] ?? "-",
        temperature: stream["T_K"] ?? "-",
        pressure: stream["P_Pa"] ?? "-",
        vaporFrac: stream["vf"] ?? "-",
        enthalpy: stream["H"] ?? "-",
        volFlowNm3h: stream["f_nm3_per_h"] ?? "-",
        volFlowM3h: stream["f_m3_per_h"] ?? "-",
        volFlowLh: stream["f_l_per_h"] ?? "-",
        molarFrac,
        massFrac,
        molarFlowComp,
        massFlowComp,
        massConc,
        compareData,
      };
    });
  }, [compareItems, parseCompareStreamData]);

  // 解析输入数据
  const analyInputData = useCallback(() => {
    if (!inputData || !resultsData || !streamsData) return;

    if (inputData.length === 1) {
      // 构建流股名称到 rule 的映射（用于判断塔顶/塔底）
      const streamRules: { [key: string]: string } = {};
      inputData[0].outputStreams.forEach((s: any) => {
        streamRules[s.name] = s.rule;
      });
      const inputStreamNames = inputData[0].inputStreams.map((s: any) => s.name);
      const outputStreamNames = inputData[0].outputStreams.map((s: any) => s.name);

      const inputResults = resultsData.filter((r: any) =>
        inputStreamNames.includes(r.name)
      );
      const outputResults = resultsData.filter((r: any) =>
        outputStreamNames.includes(r.name)
      );

      // 校验材料长度匹配
      const allStreams = [...inputResults, ...outputResults];
      const isAllMatched = allStreams.every((r: any) =>
        streamsData.materials.length === r?.["molar_frac"]?.length &&
        streamsData.materials.length === r?.["mass_frac"]?.length
      );

      if (!isAllMatched) {
        console.error("材料长度不匹配");
        return;
      }

      // 获取材料名称列表
      const materialNames = streamsData.materials.map((mat: any) => findMaterial(mat));

      const newData: DataType = {
        温度: {
          T_K: [{ name: "温度", unit: "K" }],
          T_C: [{ name: "温度", unit: "℃" }],
        },
        压力: {
          P_Pa: [{ name: "压力", unit: "Pa" }],
          P_bar: [{ name: "压力", unit: "bar" }],
          P_KPa: [{ name: "压力", unit: "KPa" }],
          P_MPa: [{ name: "压力", unit: "MPa" }],
          P_atm: [{ name: "压力", unit: "atm" }],
        },
        inputStreams: parseStreamData(inputResults, streamsData),
        outputStreams: parseStreamData(outputResults, streamsData, true, streamRules),
        materialNames,
      };

      setData(newData);
    }
  }, [inputData, resultsData, streamsData, parseStreamData]);

  useEffect(() => {
    analyInputData();
  }, [analyInputData]);

  // 默认关键组分：有甲醇或水则仅勾选甲醇/水，否则全选（仅在首次初始化）
  useEffect(() => {
    if (data?.materialNames?.length && keyComponents.length === 0 && !keyComponentsEverSet.current) {
      //甲醇水
      // const waterMethanolNames = data.materialNames.filter(
      //   (mat: string) => mat.includes("CH3OH") || mat.includes("CH₃OH") || mat.includes("H2O") || mat.includes("H₂O")
      // );
      // const initial = waterMethanolNames.length > 0 ? waterMethanolNames : data.materialNames;
      // setKeyComponents(initial);
      //全选
      setKeyComponents(data.materialNames);
      keyComponentsEverSet.current = true;
    }
  }, [data, keyComponents.length, setKeyComponents]);

  // 数值展示：有效数字格式化（非 0 值不会显示成 0，极小/极大值用科学计数法），sig 由字段内自适应精度决定
  const formatValue = (value: any, sig: number = 6) => {
    if (value === undefined || value === null || isNaN(value)) {
      return "-";
    }
    return formatBySig(value, sig);
  };

  // 组分数值格式化（原数据是什么就显示什么，仅处理无效值；非 0 的极小值同样按有效数字展示）
  const formatComponentValue = (value: any, sig: number = 6) => {
    if (value === undefined || value === null || isNaN(value)) {
      return "-";
    }
    return formatBySig(value, sig);
  };

  // 根据selectButton获取对应的数据字段
  const getComponentData = (stream: SingleStreamData, matName: string, compareId?: number) => {
    if (compareId !== undefined && stream.compareData[compareId]) {
      switch (selectButton) {
        case "摩尔分率":
          return stream.compareData[compareId].molarFrac[matName];
        case "质量分率":
          return stream.compareData[compareId].massFrac[matName];
        case "质量浓度":
          return stream.compareData[compareId].massConc[matName];
        case "摩尔流量":
          return stream.compareData[compareId].molarFlowComp[matName];
        case "质量流量":
          return stream.compareData[compareId].massFlowComp[matName];
        default:
          return 0;
      }
    }
    switch (selectButton) {
      case "摩尔分率":
        return stream.molarFrac[matName];
      case "质量分率":
        return stream.massFrac[matName];
      case "质量浓度":
        return stream.massConc[matName];
      case "摩尔流量":
        return stream.molarFlowComp[matName];
      case "质量流量":
        return stream.massFlowComp[matName];
      default:
        return 0;
    }
  };

  // ========== ✅ 流股基础属性表格（和截图样式完全一致，用原生table实现双层表头） ==========
  const renderStreamPropertyTable = (streams: SingleStreamData[], title: string) => {
    if (!data) return null;
    const compareCount = compareItems.length;
    const baseFields = [
      { key: "temperature", label: `温度（${data.温度[effectiveSelectT][0]["unit"]}）` },
      { key: "pressure", label: `压力（${data.压力[effectiveSelectP][0]["unit"]}）` },
      { key: "molarFlow", label: "摩尔流量（kmol/h）" },
      { key: "massFlow", label: "质量流量（kg/h）" },
      { key: "volFlowNm3h", label: "气相体积流量（Nm³/h）" },
      { key: "volFlowLiquid", label: "液相体积流量" },
      { key: "vaporFrac", label: "气化率" },
      { key: "enthalpy", label: "焓（J/mol）" },
    ];

    // 辅助：获取某个 stream 某个 field 的计算值
    const getCalcVal = (stream: SingleStreamData, fieldKey: string): any => {
      switch (fieldKey) {
        case "temperature":
          return effectiveSelectT === "T_K" ? +(stream.temperature).toFixed(2) : +(stream.temperature - 273.15).toFixed(2);
        case "pressure": {
          const p = stream.pressure;
          if (effectiveSelectP === "P_bar") return +(p / 100000).toFixed(2);
          if (effectiveSelectP === "P_KPa") return +(p / 1000).toFixed(2);
          if (effectiveSelectP === "P_MPa") return +(p / 1000000).toFixed(2);
          if (effectiveSelectP === "P_atm") return +(p / 101325).toFixed(2);
          return +p.toFixed(2);
        }
        case "molarFlow": return stream.molarFlow;
        case "massFlow": return stream.massFlow;
        case "volFlowNm3h": return stream.volFlowNm3h;
        case "volFlowLiquid": return selectVolFlow === "volFlowM3h" ? stream.volFlowM3h : stream.volFlowLh;
        case "vaporFrac": return stream.vaporFrac;
        case "enthalpy": return stream.enthalpy;
        default: return 0;
      }
    };

    // 辅助：安全转数字，无效值（null/undefined/非数字/缺失）返回 null
    const safeNum = (v: any): number | null => {
      if (v === null || v === undefined || v === "-" || v === "") return null;
      const n = Number(v);
      return isNaN(n) ? null : n;
    };

    // 辅助：获取某个 stream 某个 field 的对比值
    const getCompareVal = (stream: SingleStreamData, fieldKey: string, compareId: number): any => {
      const cd = stream.compareData[compareId];
      if (!cd) return null;
      switch (fieldKey) {
        case "temperature": {
          const t = safeNum(cd.temperature);
          if (t === null) return "-";
          return effectiveSelectT === "T_K" ? +(t + 273.15).toFixed(2) : +t.toFixed(2);
        }
        case "pressure": {
          const cp = safeNum(cd.pressure);
          if (cp === null) return "-";
          if (effectiveSelectP === "P_bar") return +(cp / 100000).toFixed(2);
          if (effectiveSelectP === "P_KPa") return +(cp / 1000).toFixed(2);
          if (effectiveSelectP === "P_MPa") return +(cp / 1000000).toFixed(2);
          if (effectiveSelectP === "P_atm") return +(cp / 101325).toFixed(2);
          return +cp.toFixed(2);
        }
        case "molarFlow": return cd.molarFlow;
        case "massFlow": return cd.massFlow;
        case "volFlowNm3h": return cd.volFlowNm3h;
        case "volFlowLiquid": return selectVolFlow === "volFlowM3h" ? cd.volFlowM3h : cd.volFlowLh;
        case "vaporFrac": return cd.vaporFrac;
        case "enthalpy": return cd.enthalpy;
        default: return null;
      }
    };

    // 辅助：检查一个值是否为 0 或无效
    const isZeroOrInvalid = (val: any): boolean => {
      if (val === null || val === undefined || val === "-") return true;
      return Number(val) === 0;
    };

    // 辅助：判断某一行（stream）的所有 field 是否全为 0/无效
    const isRowAllZero = (stream: SingleStreamData): boolean => {
      return baseFields.every((field) => {
        const cv = getCalcVal(stream, field.key);
        if (!isZeroOrInvalid(cv)) return false;
        // 检查该 field 的所有对比值
        for (let i = 0; i < compareCount; i++) {
          const cmpId = compareItems[i]?.id;
          if (cmpId !== undefined) {
            const cv2 = getCompareVal(stream, field.key, cmpId);
            if (!isZeroOrInvalid(cv2)) return false;
          }
        }
        return true;
      });
    };

    // 辅助：判断某一列（field）在所有 stream 中是否全为 0/无效
    const isColumnAllZero = (fieldKey: string): boolean => {
      return streams.every((stream) => {
        const cv = getCalcVal(stream, fieldKey);
        if (!isZeroOrInvalid(cv)) return false;
        for (let i = 0; i < compareCount; i++) {
          const cmpId = compareItems[i]?.id;
          if (cmpId !== undefined) {
            const cv2 = getCompareVal(stream, fieldKey, cmpId);
            if (!isZeroOrInvalid(cv2)) return false;
          }
        }
        return true;
      });
    };

    // 过滤行和列
    let visibleStreams = streams;
    let visibleFields = baseFields;
    if (!showAllData) {
      visibleStreams = streams.filter((s) => !isRowAllZero(s));
      visibleFields = baseFields.filter((f) => !isColumnAllZero(f.key));
      if (visibleStreams.length === 0 || visibleFields.length === 0) return null;
    }
console.log("visibleFields",visibleStreams);
    return (
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 16 }}>{title}：</div>
        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ tableLayout: 'auto', width: '100%' }}>
            <thead>
              <tr className="table-header">
                <th className="table-header-cell stream-col" rowSpan={2} style={{ minWidth: "120px" }}>流股</th>
                <th className="table-header-cell" rowSpan={2} style={{ minWidth: "120px", maxWidth: "300px" }}>流股描述</th>
                <th className="table-header-cell" rowSpan={2} style={{ minWidth: "100px" }}>源</th>
                <th className="table-header-cell" rowSpan={2} style={{ minWidth: "100px" }}>目标</th>
                <th className="table-header-cell" rowSpan={2} style={{ minWidth: "80px" }}>相态</th>
                {visibleFields.map((field) => (
                  <th key={field.key} className="table-header-cell" colSpan={1 + compareCount} style={{ minWidth: "100px" }}>
                    {field.key === "volFlowLiquid" ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {field.label}
                        <Select
                          size="small"
                          value={selectVolFlow}
                          onChange={(v) => setSelectVolFlow(v)}
                          style={{ marginLeft:10,width: 70 }}
                          options={[
                            { value: "volFlowM3h", label: "m³/h" },
                            { value: "volFlowLh", label: "L/h" },
                          ]}
                        />
                      </span>
                    ) : (
                      field.label
                    )}
                  </th>
                ))}
              </tr>
              {compareCount > 0 && (
                <tr className="table-header">
                  {visibleFields.map((field) => (
                    <React.Fragment key={field.key}>
                      <th className="table-header-cell sub-header" style={{ minWidth: "60px" }}>当前计算值</th>
                      {compareItems.map((item, idx) => (
                        <th key={idx} className="table-header-cell sub-header" style={{ minWidth: "80px" }}>
                          {item.type === "task" 
                            ? item.values.name 
                            : item.type === "otherCalc"
                            ? `${item.values.name} (${item.created_at ? dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss") : ''})`
                            : item.type === "design" ? `设计值 (${item.id})` : (item.name || item.values?.name) || (item.type === "timeRange" ? (item.fullDay ? `运行数据 ${item.date}` : `运行数据 ${item.startTime} ~ ${item.endTime}`) : `运行数据 (${item.id})`)}
                        </th>
                      ))}
                    </React.Fragment>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {visibleStreams.map((stream, sIdx) => {
                return (
                  <tr key={sIdx} className="table-row">
                    <td className="table-cell stream-col" style={{ minWidth: "120px" }}>{stream.streamName}</td>
                    <td className="table-cell" style={{ minWidth: "120px" }}>{streamDescMap[stream.originStreamName] || "—"}</td>
                    <td className="table-cell" style={{ minWidth: "100px" }}>
                      <Tooltip title={elementDescMap[streamNodeMap[stream.originStreamName]?.fromName] || "暂无描述"}>
                        <span style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}>{streamNodeMap[stream.originStreamName]?.fromName || "—"}</span>
                      </Tooltip>
                    </td>
                    <td className="table-cell" style={{ minWidth: "100px" }}>
                      <Tooltip title={elementDescMap[streamNodeMap[stream.originStreamName]?.toName] || "暂无描述"}>
                        <span style={{ cursor: "pointer", borderBottom: "1px dashed #999" }}>{streamNodeMap[stream.originStreamName]?.toName || "—"}</span>
                      </Tooltip>
                    </td>
                    <td className="table-cell" style={{ minWidth: "80px" }}>{stream.phase}</td>
                    {visibleFields.map((field) => {
                      const calcVal = getCalcVal(stream, field.key);
                      const compareVals = compareItems.map((item) => {
                        const compareData = stream.compareData[item.id];
                        return compareData ? getCompareVal(stream, field.key, item.id) : null;
                      });
                      // 该字段下任一对比值与计算值有差异，则该字段的计算值+所有对比值单元格标红
                      const fieldHasDiff = compareCount > 0 && compareVals.some((v) => valuesDiffer(calcVal, v));
                      // 该字段内自适应精度：常态 6 位有效数字，有差异时提升精度，保证差异在显示上也能看出来
                      const sig = resolveSig([calcVal, ...compareVals], valuesDiffer);
                      return (
                        <React.Fragment key={field.key}>
                          <td className={`table-cell ${fieldHasDiff ? "compare-cell-diff" : ""}`} style={{ minWidth: "100px" }} title={String(calcVal)}>{formatValue(calcVal, sig)}</td>
                          {compareCount > 0 && compareItems.map((item, cIdx) => {
                            const compareVal = compareVals[cIdx];
                            const errorPercent = isNumValue(compareVal) && isNumValue(calcVal)
                              ? calculateErrorPercent(Number(compareVal), Number(calcVal))
                              : "-";
                            return (
                              <td key={cIdx} className={`table-cell ${fieldHasDiff ? "compare-cell-diff" : ""}`} style={{ minWidth: "80px" }} title={String(compareVal)}>
                                {formatValue(compareVal, sig)}{errorPercent !== "-" ? `(${errorPercent})` : ""}
                              </td>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ========== 组分表格（原生table双层表头，和截图一致） ==========
  const renderComponentTable = (streams: SingleStreamData[], title: string) => {
    if (!data) return null;
    const compareCount = compareItems.length;

    // 辅助：检查一个值是否为 0 或无效
    const isZeroOrInvalid = (val: any): boolean => {
      if (val === null || val === undefined || val === "-") return true;
      return Number(val) === 0;
    };

    // 判断某一行（stream）的所有材料列是否全为 0/无效
    const isRowAllZero = (stream: SingleStreamData): boolean => {
      return data.materialNames.every((matName) => {
        const cv = getComponentData(stream, matName);
        if (!isZeroOrInvalid(cv)) return false;
        for (let i = 0; i < compareCount; i++) {
          const cmpId = compareItems[i]?.id;
          if (cmpId !== undefined) {
            const cv2 = getComponentData(stream, matName, cmpId);
            if (!isZeroOrInvalid(cv2)) return false;
          }
        }
        return true;
      });
    };

    // 判断某一列（材料）在所有 stream 中是否全为 0/无效
    const isColumnAllZero = (matName: string): boolean => {
      return streams.every((stream) => {
        const cv = getComponentData(stream, matName);
        if (!isZeroOrInvalid(cv)) return false;
        for (let i = 0; i < compareCount; i++) {
          const cmpId = compareItems[i]?.id;
          if (cmpId !== undefined) {
            const cv2 = getComponentData(stream, matName, cmpId);
            if (!isZeroOrInvalid(cv2)) return false;
          }
        }
        return true;
      });
    };

    // 过滤行和列
    let visibleStreams = streams;
    let visibleMaterials = data.materialNames;
    if (!showAllData) {
      visibleStreams = streams.filter((s) => !isRowAllZero(s));
      visibleMaterials = data.materialNames.filter((mat) => !isColumnAllZero(mat) || keyComponents.includes(mat));
    }
    // 仅显示关键组分
    visibleMaterials = visibleMaterials.filter((mat) => keyComponents.includes(mat));
    const hasData = visibleStreams.length > 0 && visibleMaterials.length > 0;

    return (
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 16 }}>{title}：</div>
        <div className="mode-selector-container" style={{ position: 'relative' }}>
            <span className="mode-selector-label">显示数据切换：</span>
            <div className="mode-buttons">
              {DATA_TYPE_ITEMS.map(item => (
                <button
                  key={item.value}
                  className={`mode-button ${selectButton === item.value ? "active" : ""}`}
                  onClick={() => setSelectButton(item.value)}
                >
                  {item.label}{item.unit ? `(${item.unit})` : ""}
                </button>
              ))}
            </div>
            <Dropdown
              trigger={["click"]}
              overlay={
                <div style={{ padding: 12, background: "#fff", minWidth: 200 }}>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>选择关键组分：</div>
                  <Checkbox.Group
                    value={keyComponents}
                    onChange={(checkedValues) => setKeyComponents(checkedValues as string[])}
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {data?.materialNames?.map((mat: string) => (
                      <Checkbox key={mat} value={mat}>
                        {replaceFormulaStr(mat)}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                  <div style={{ marginTop: 8, borderTop: "1px solid #f0f0f0", paddingTop: 8, display: "flex", gap: 12 }}>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setKeyComponents(data?.materialNames || [])}
                    >
                      全选
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setKeyComponents([])}
                    >
                      取消全选
                    </Button>
                  </div>
                </div>
              }
            >
              <Button style={{ fontSize: 14, position: "absolute", right: "0", top: 0, display: "inline-flex", gap: 0 }}>
                <span style={{ fontSize: 18 }}>⚙️</span>设置关键组分
              </Button>
            </Dropdown>
        </div>
        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          {hasData && (
          <table className="component-data-table" style={{ tableLayout: 'auto', width: '100%' }}>
            <thead>
              <tr className="table-header">
                <th className="table-header-cell stream-col" rowSpan={2} style={{ minWidth: "120px", maxWidth: "200px" }}>流股</th>
                {/* <th className="table-header-cell" rowSpan={2} style={{ minWidth: "120px", maxWidth: "300px" }}>流股描述</th> */}
                {visibleMaterials.map((mat, idx) => (
                  <th
                    key={mat}
                    className="table-header-cell"
                    colSpan={compareCount > 0 ? 1 + compareCount : 1}
                    style={{ minWidth: "80px" }}
                  >
                    {replaceFormulaStr(mat)}
                  </th>
                ))}
              </tr>
              {compareCount > 0 && (
                <tr className="table-header">
                  {visibleMaterials.map((mat) => (
                    <React.Fragment key={mat}>
                      <th className="table-header-cell sub-header" style={{ minWidth: "60px" }}>当前计算值</th>
                      {compareItems.map((item, i) =>{
                        return (
                        <th key={i} className="table-header-cell sub-header" style={{ minWidth: "80px" }}>
                          {item.type === "task" 
                            ? item.values.name 
                            : item.type === "otherCalc"
                            ? `${item.values.name} (${item.created_at ? dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss") : ''})`
                            : item.type === "design" ? `设计值 (${item.id})` : (item.name || item.values?.name) || (item.type === "timeRange" ? (item.fullDay ? `运行数据 ${item.date}` : `运行数据 ${item.startTime} ~ ${item.endTime}`) : `运行数据 (${item.id})`)}
                        </th>
                      )} )}
                    </React.Fragment>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {visibleStreams.map((stream, sIdx) => {
                return (
                  <tr key={sIdx} className="table-row">
                    <td className="table-cell stream-col" style={{ minWidth: "120px" }}>{stream.streamName}</td>
                    {/* <td className="table-cell" style={{ minWidth: "120px" }}>{streamDescMap[stream.originStreamName] || "—"}</td> */}
                    {visibleMaterials.map((matName) => {
                      const calcValue = getComponentData(stream, matName);
                      const compareValues = compareItems.map((item) => {
                        const compareData = stream.compareData[item.id];
                        return compareData ? getComponentData(stream, matName, item.id) : null;
                      });
                      // 该组分下任一对比值与计算值有差异，则该组分的计算值+所有对比值单元格标红
                      const matHasDiff = compareCount > 0 && compareValues.some((v) => valuesDiffer(calcValue, v));
                      // 该组分内自适应精度：常态 6 位有效数字，有差异时提升精度，保证差异在显示上也能看出来
                      const sig = resolveSig([calcValue, ...compareValues], valuesDiffer);
                      return (
                        <React.Fragment key={matName}>
                          <td className={`table-cell ${matHasDiff ? "compare-cell-diff" : ""}`} style={{ minWidth: "80px" }} title={String(calcValue)}>
                            {formatComponentValue(calcValue, sig)}
                          </td>
                          {compareCount > 0 && compareItems.map((item, i) => {
                            // 对比项缺失时按缺失处理（显示 -）
                            const compareValue = compareValues[i];
                            const errorPercent = (compareValue === null || compareValue === undefined)
                              ? "-"
                              : (compareValue !== 0)
                              ? calculateErrorPercent(Number(compareValue), Number(calcValue))
                              : (calcValue === 0) ? "0.00%" : "100.00%";
                            return (
                              <td key={i} className={`table-cell ${matHasDiff ? "compare-cell-diff" : ""}`} style={{ minWidth: "80px" }} title={String(compareValue)}>
                                {formatComponentValue(compareValue, sig)}{errorPercent !== "-" ? `(${errorPercent})` : ""}
                              </td>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      </div>
    );
  };

  if (!data) return <div className="data-empty">暂无计算数据</div>;

  return (
    <div className="calculate-task-table-container">
      {/* 温压切换（仅在无外部控制时显示） */}
      {!tempUnit && (
      <div className="data-type-buttons" style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 16, marginRight: 12, lineHeight: "30px" }}>温度单位：</span>
        {T_ITEMS.map(item => (
          <button
            key={item.value}
            className={`type-button ${selectT === item.value ? "active" : ""}`}
            onClick={() => setSelectT(item.value)}
          >
            {item.label}
          </button>
        ))}
        <span style={{ fontSize: 16, margin: "0 12px", lineHeight: "30px" }}>压力单位：</span>
        {P_ITEMS.map(item => (
          <button
            key={item.value}
            className={`type-button ${selectP === item.value ? "active" : ""}`}
            onClick={() => setSelectP(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>
      )}
      {/* 工艺数据部分 */}
      {(data.inputStreams.length > 0 || data.outputStreams.length > 0) && (
        <>
          {renderStreamPropertyTable([...data.inputStreams, ...data.outputStreams], "工艺数据")}
        </>
      )}

      {/* 组分数据部分 */}
      {(data.inputStreams.length > 0 || data.outputStreams.length > 0) && (
        <>
          {renderComponentTable([...data.inputStreams, ...data.outputStreams], "组分数据")}
        </>
      )}
    </div>
  );
}